import React, { useState, useEffect, useRef } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import {
  ArrowRight, Check, ChevronDown, Download, Menu, X, Target, Lightbulb,
  TrendingUp, Handshake, Cloud, Server, Brain, Code2, Shield, Database,
  Clock, Award, Users, Star, Quote, Sparkles, Zap, Building2, Play,
  ArrowUpRight, CircleDot, Layers, GitBranch, Rocket,
} from 'lucide-react';
import {Link} from 'react-router-dom';

// ============ COLOR PALETTE ============
const COLORS = {
  primary: '#001534',
  secondary: '#FFFFFF',
  accent: '#6366F1',
  accentLight: '#818CF8',
  accentDark: '#4F46E5',
  purple: '#A855F7',
  cyan: '#06B6D4',
  white: '#FFFFFF',
  black: '#000000',
  gray50: '#F9FAFB',
  gray100: '#F3F4F6',
  gray200: '#E5E7EB',
  gray300: '#D1D5DB',
  gray400: '#9CA3AF',
  gray500: '#6B7280',
  gray600: '#4B5563',
  gray700: '#374151',
  gray800: '#1F2937',
  gray900: '#111827',
  gradientStart: '#6366F1',
  gradientEnd: '#A855F7',
};

// ============ ANIMATED COUNTER ============
const AnimatedCounter = ({ end, suffix = '', duration = 2 }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const increment = end / (duration * 60);
    const timer = setInterval(() => {
      start += increment;
      if (start >= end) { setCount(end); clearInterval(timer); }
      else setCount(Math.floor(start));
    }, 1000 / 60);
    return () => clearInterval(timer);
  }, [inView, end, duration]);

  return <span ref={ref}>{count}{suffix}</span>;
};


// ============ HERO ============
const Hero = () => {
  return (
    <section
      className="relative flex items-center overflow-hidden min-h-[90vh]"
      style={{ backgroundColor: COLORS.primary }}
    >
      {/* Background gradients */}
      <div className="absolute inset-0 overflow-hidden">
        <div
          className="absolute top-0 left-1/4 w-[600px] h-[600px] rounded-full opacity-20 blur-[120px]"
          style={{ backgroundColor: COLORS.accent }}
        />
        <div
          className="absolute bottom-0 right-1/4 w-[500px] h-[500px] rounded-full opacity-20 blur-[120px]"
          style={{ backgroundColor: COLORS.purple }}
        />
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `linear-gradient(${COLORS.white} 1px, transparent 1px), linear-gradient(90deg, ${COLORS.white} 1px, transparent 1px)`,
            backgroundSize: '60px 60px',
          }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 w-full">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-semibold leading-[1.05] tracking-wide mb-6">
              <span style={{ color: COLORS.white }}>Permanent IT Staffing</span>
              <br />
              <span style={{ color: COLORS.white }}>Solutions That </span>
              <span
                className="relative inline-block"
                style={{
                  background: `linear-gradient(135deg, ${COLORS.accentLight}, ${COLORS.purple})`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Drive Growth
              </span>
            </h1>

            <p className="text-md md:text-xl leading-relaxed mb-10 max-w-xl" style={{ color: 'rgba(255,255,255,0.7)' }}>
              Connect with top-tier tech talent. Build teams that grow with your business. Expert placement in 2-4 weeks.
            </p>

            <div className="flex flex-wrap gap-4 mb-12">
              <button
                className="group flex items-center gap-2 px-7 py-4 rounded-xl font-semibold text-base transition-all hover:shadow-2xl hover:shadow-indigo-500/40"
                style={{
                  background: `linear-gradient(135deg, ${COLORS.accent}, ${COLORS.purple})`,
                  color: COLORS.white,
                }}
              >
                Get Started
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </button>
              <button
                className="flex items-center gap-2 px-7 py-4 rounded-xl font-semibold text-base border transition-all"
                style={{
                  backgroundColor: 'rgba(255,255,255,0.05)',
                  borderColor: 'rgba(255,255,255,0.2)',
                  color: COLORS.white,
                }}
              >
                See Our Process
              </button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { value: 85, suffix: '%', label: 'Placement Success Rate' },
                { value: 4, suffix: ' wks', label: 'Average Placement Time', prefix: '2-' },
                { value: 500, suffix: '+', label: 'Tech Professionals Placed' },
                { value: 95, suffix: '%', label: 'Client Satisfaction' },
              ].map((stat, i) => (
                <div
                  key={i}
                  className="border-l-2 pl-4"
                  style={{ borderColor: COLORS.accent }}
                >
                  <div className="text-3xl font-semibold" style={{ color: COLORS.white }}>
                    {stat.prefix}<AnimatedCounter end={stat.value} suffix={stat.suffix} />
                  </div>
                  <div className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.6)' }}>{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Hero Image */}
          <div className="lg:col-span-6 relative">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl h-[400px]">
              <img
                src="https://ik.imagekit.io/7wqj9br0b/KapilTech_Website_Staffing/Staff%20Augementation/permenantstaffing.jpg"
                alt="Tech team collaboration"
                className="w-full h-full object-fit"
                loading="lazy"
              />
              <div
                className="absolute inset-0"
                style={{
                  background: `linear-gradient(135deg, rgba(99, 102, 241, 0.3), rgba(168, 85, 247, 0.2))`,
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ============ WHY PERMANENT STAFFING ============
const WhyStaffing = () => {
  const features = [
    {
      icon: Target,
      title: 'Cultural Fit & Alignment',
      desc: 'We go beyond skills and experience. Our rigorous vetting process identifies professionals who share your values, communication style, and commitment to excellence.',
      color: COLORS.accent,
      size: 'large',
    },
    {
      icon: Lightbulb,
      title: 'Access Specialized Expertise',
      desc: 'Our extensive network spans cloud platforms (AWS, Azure, GCP), DevOps, AI/ML, full-stack development, cybersecurity, and emerging technologies. We pre-screen for technical depth and certifications.',
      color: COLORS.purple,
      size: 'large',
    },
    {
      icon: TrendingUp,
      title: 'Build Sustainable Talent Pipeline',
      desc: 'Permanent placements create stability for long-term growth. You retain institutional knowledge, develop future leaders, and build organizational resilience that scales with your ambitions.',
      color: COLORS.cyan,
      size: 'small',
    },
    {
      icon: Handshake,
      title: 'Strategic Partnership Approach',
      desc: "We're invested in your success. Our tech-savvy recruiters provide ongoing guidance, market insights, and pre-placement technical assessments to ensure perfect fits.",
      color: '#F59E0B',
      size: 'small',
    },
  ];

  return (
    <section
      className="relative py-16 overflow-hidden"
      style={{ backgroundColor: COLORS.secondary }}
      id="services"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-7xl mx-auto mb-20"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold leading-tight mb-6" style={{ color: COLORS.primary }}>
            Why Permanent Staffing Matters for{' '}
              Tech Teams
          </h2>
          <p className="text-lg leading-relaxed" style={{ color: COLORS.gray600 }}>
            Finding the right talent isn't just about filling positions—it's about building a team that grows with your organization. Permanent hires bring stability, institutional knowledge, and long-term commitment that accelerates innovation and reduces turnover costs.
          </p>
        </motion.div>

        {/* Bento Grid */}
        <div className="grid md:grid-cols-2 gap-6">
          {features.map((feat, i) => {
            const Icon = feat.icon;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                whileHover={{ y: -4 }}
                className="group relative p-8 md:p-10 rounded-3xl border transition-all overflow-hidden"
                style={{
                  backgroundColor: COLORS.gray50,
                  borderColor: COLORS.gray200,
                }}
              >
                <div
                  className="absolute -top-20 -right-20 w-60 h-60 rounded-full opacity-10 blur-3xl transition-opacity group-hover:opacity-20"
                  style={{ backgroundColor: feat.color }}
                />

                <div className="relative">
                  <div
                    className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6"
                    style={{
                      background: `linear-gradient(135deg, ${feat.color}30, ${feat.color}10)`,
                      border: `1px solid ${feat.color}40`,
                    }}
                  >
                    <Icon className="w-7 h-7" style={{ color: feat.color }} />
                  </div>

                  <h3 className="text-2xl font-bold mb-4" style={{ color: COLORS.primary }}>
                    {feat.title}
                  </h3>
                  <p className="leading-relaxed" style={{ color: COLORS.gray600 }}>
                    {feat.desc}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// ============ TECHNOLOGY EXPERTISE ============
const TechExpertise = () => {
  const [active, setActive] = useState(0);

const specializations = [
  {
    icon: Cloud,
    title: 'Cloud & Infrastructure',
    desc: 'AWS, Azure, GCP architects and engineers. Infrastructure as Code, containerization, Kubernetes expertise. We help organizations build secure, scalable cloud environments with certified professionals who optimize performance, reliability, and operational efficiency.',
    tags: ['AWS', 'Azure', 'GCP', 'Kubernetes', 'Docker'],
    color: COLORS.cyan,
    img: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=800&q=80',
  },
  {
    icon: GitBranch,
    title: 'DevOps & SRE',
    desc: 'CI/CD pipeline architects, infrastructure automation, monitoring, and reliability engineers. Our specialists streamline software delivery, improve deployment speed, and ensure highly available, resilient systems through modern DevOps practices.',
    tags: ['Jenkins', 'GitLab CI', 'Terraform', 'Prometheus'],
    color: COLORS.cyan,
    img: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=800&q=80',
  },
  {
    icon: Brain,
    title: 'AI & Machine Learning',
    desc: 'ML engineers, data scientists, NLP specialists with deep learning and ML frameworks expertise. From predictive analytics to generative AI solutions, we connect businesses with experts capable of transforming complex data into valuable insights.',
    tags: ['Python', 'TensorFlow', 'PyTorch', 'Data Science'],
    color: COLORS.cyan,
    img: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800&q=80',
  },
  {
    icon: Code2,
    title: 'Full-Stack Development',
    desc: 'React, Vue, Angular frontend experts and backend engineers proficient in Node.js, Java, Python. We source developers experienced in building scalable web applications, APIs, and enterprise-grade software with modern development frameworks.',
    tags: ['React', 'Node.js', 'Java', 'Python'],
    color: COLORS.cyan,
    img: 'https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=800&q=80',
  },
  {
    icon: Shield,
    title: 'Cybersecurity & Compliance',
    desc: 'Security engineers, penetration testers, compliance specialists with CISSP, CEH certifications. Our experts strengthen your security posture by protecting critical infrastructure, ensuring regulatory compliance, and mitigating evolving cyber threats.',
    tags: ['Security', 'Compliance', 'CISSP', 'SOC'],
    color: COLORS.cyan,
    img: 'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=800&q=80',
  },
  {
    icon: Database,
    title: 'Data Engineering',
    desc: 'Data pipeline architects, ETL engineers, big data specialists with Spark, Snowflake expertise. We provide professionals who design reliable data ecosystems, enabling organizations to process, analyze, and leverage data at enterprise scale.',
    tags: ['Spark', 'Snowflake', 'BigQuery', 'Airflow'],
    color: COLORS.cyan,
    img: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80',
  },
];

  const current = specializations[active];

  return (
    <section
      className="relative py-16 overflow-hidden"
      style={{ backgroundColor: COLORS.primary }}
      id="expertise"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center max-w-5xl mx-auto mb-16"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold leading-tight mb-6" style={{ color: COLORS.white }}>
            Technology Expertise &
              Specializations
          </h2>
          <p className="text-lg text-center" style={{ color: 'rgba(255,255,255,0.7)' }}>
            We specialize across the full spectrum of modern tech roles and emerging technologies.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-12 gap-8">
          {/* Tabs */}
          <div className="lg:col-span-5 space-y-2">
            {specializations.map((spec, i) => {
              const Icon = spec.icon;
              const isActive = active === i;
              return (
                <motion.button
                  key={i}
                  onClick={() => setActive(i)}
                  whileHover={{ x: 4 }}
                  className="w-full flex items-center gap-4 p-5 rounded-2xl text-left transition-all border"
                  style={{
                    backgroundColor: isActive ? 'rgba(99, 102, 241, 0.15)' : 'rgba(255,255,255,0.03)',
                    borderColor: isActive ? `${spec.color}60` : 'rgba(255,255,255,0.1)',
                  }}
                >
                  <div
                    className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{
                      backgroundColor: isActive ? `${spec.color}30` : 'rgba(255,255,255,0.05)',
                    }}
                  >
                    <Icon className="w-5 h-5" style={{ color: isActive ? spec.color : 'rgba(255,255,255,0.5)' }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-3xl md:text-lg font-semibold" style={{ color: isActive ? COLORS.white : 'rgba(255,255,255,0.7)' }}>
                      {spec.title}
                    </div>
                  </div>
                  {isActive && <ArrowRight className="w-4 h-4" style={{ color: spec.color }} />}
                </motion.button>
              );
            })}
          </div>

          {/* Content */}
          <div className="lg:col-span-7">
            <AnimatePresence mode="wait">
              <motion.div
                key={active}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4 }}
                className="relative rounded-3xl overflow-hidden border h-full"
                style={{
                  backgroundColor: 'rgba(255,255,255,0.03)',
                  borderColor: 'rgba(255,255,255,0.1)',
                  minHeight: '500px',
                }}
              >
                <div className="absolute inset-0">
                  <div
                    className="absolute inset-0"
                    style={{
                      background: `linear-gradient(135deg, ${COLORS.primary}ee, ${COLORS.primary}80)`,
                    }}
                  />
                </div>

                <div className="relative p-10 h-full flex flex-col">
                  <div>
                    <div
                      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold mb-6"
                      style={{ backgroundColor: `${current.color}30`, color: current.color }}
                    >
                      <CircleDot className="w-3 h-3" />
                      {current.title}
                    </div>
                    <h3 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: COLORS.white }}>
                      {current.title}
                    </h3>
                    <p className="text-lg leading-relaxed mb-8 max-w-lg" style={{ color: 'rgba(255,255,255,0.7)',wordBreak: 'normal',overflowWrap: 'normal',hyphens: 'none',whieSpace: 'normal' }}>
                      {current.desc}
                    </p>
                  </div>

                  <div>
                    <div className="text-xs font-semibold tracking-wider uppercase mb-4" style={{ color: 'rgba(255,255,255,0.5)' }}>
                      Key Technologies
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {current.tags.map((tag, i) => (
                        <motion.span
                          key={tag}
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: i * 0.05 }}
                          className="px-4 py-2 rounded-lg text-sm font-medium border"
                          style={{
                            backgroundColor: 'rgba(255,255,255,0.05)',
                            borderColor: 'rgba(255,255,255,0.15)',
                            color: 'rgba(255,255,255,0.9)',
                          }}
                        >
                          {tag}
                        </motion.span>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};

// ============ PROCESS TIMELINE ============
const Process = () => {
  const steps = [
    {
      week: 'Week 1',
      title: 'Discovery & Strategy Session',
      desc: 'Initial consultation to understand your hiring needs, team dynamics, tech stack, company culture, and growth roadmap. We gather comprehensive requirements to create a targeted recruitment strategy.',
      icon: Target,
      color: COLORS.accent,
    },
    {
      week: 'Week 2',
      title: 'Candidate Sourcing & Assessment',
      desc: 'Our team sources candidates from our curated talent network and conducts technical assessments, skills evaluation, and culture fit analysis. All candidates are pre-vetted with portfolio reviews and technical depth assessment.',
      icon: Users,
      color: COLORS.purple,
    },
    {
      week: 'Week 3-4',
      title: 'Presentation & Interviews',
      desc: 'Live presentation of 3-5 qualified candidates with detailed background briefs. We coordinate interviews, provide feedback loops, and manage logistics seamlessly between your team and candidates.',
      icon: Handshake,
      color: COLORS.cyan,
    },
    {
      week: 'Offer',
      title: 'Offer & Negotiation',
      desc: 'We facilitate competitive offers, handle negotiations, manage counter-offers, and ensure smooth deal closure. Our team provides guidance on market rates and benefits packages.',
      icon: Award,
      color: '#F59E0B',
    },
    {
      week: 'Post',
      title: 'Onboarding & Aftercare',
      desc: 'Post-placement support for 90 days ensures successful integration. We remain your partner throughout, checking in with both employee and employer to celebrate wins and address any adjustments needed.',
      icon: Rocket,
      color: '#10B981',
    },
  ];

  return (
    <section
      className="relative py-16 overflow-hidden"
      style={{ backgroundColor: COLORS.secondary }}
      id="process"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center max-w-3xl mx-auto mb-20"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold leading-tight mb-6" style={{ color: COLORS.primary }}>
            Our Permanent Staffing
              Process
          </h2>
          <p className="text-lg text-center" style={{ color: COLORS.gray600 }}>
            A structured, transparent approach ensuring the best match for your team.
          </p>
        </motion.div>
        </div>

        <div className="relative max-w-7xl mx-auto">
          {/* Vertical line */}
          <div
            className="absolute left-8 md:left-1/2 top-0 bottom-0 w-px md:-translate-x-1/2"
            style={{ backgroundColor: COLORS.gray200 }}
          />

          <div className="space-y-12">
            {steps.map((step, i) => {
              const Icon = step.icon;
              const isLeft = i % 2 === 0;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: i * 0.1 }}
                  className={`relative flex items-center ${isLeft ? 'md:flex-row' : 'md:flex-row-reverse'}`}
                >
                  {/* Center dot */}
                  <div className="absolute left-8 md:left-1/2 md:-translate-x-1/2 z-10">
                    <motion.div
                      whileHover={{ scale: 1.2 }}
                      className="w-16 h-16 rounded-2xl flex items-center justify-center border-1"
                      style={{
                        backgroundColor: COLORS.white,
                        borderColor: step.color,
                        boxShadow: `0 0 30px ${step.color}40`,
                      }}
                    >
                      <Icon className="w-6 h-6" style={{ color: step.color }} />
                    </motion.div>
                  </div>

                  {/* Content */}
                  <div className={`w-full md:w-1/2 ${isLeft ? 'md:pr-20 pl-24 md:pl-0' : 'md:pl-20 pl-24 md:pr-0'}`}>
                    <motion.div
                      whileHover={{ y: -4 }}
                      className="p-8 rounded-2xl border transition-all"
                      style={{
                        backgroundColor: COLORS.gray50,
                        borderColor: COLORS.gray200,
                      }}
                    >
                      <div className="flex items-center gap-3 mb-4">
                        <span
                          className="px-3 py-1 rounded-full text-xs font-bold tracking-wider"
                          style={{ backgroundColor: `${step.color}20`, color: step.color }}
                        >
                          {step.week}
                        </span>
                        <div className="flex-1 h-px" style={{ backgroundColor: COLORS.gray200 }} />
                        <span className="text-xs font-mono" style={{ color: COLORS.gray500 }}>
                          0{i + 1}
                        </span>
                      </div>
                      <h3 className="text-2xl font-bold mb-3" style={{ color: COLORS.primary }}>
                        {step.title}
                      </h3>
                      <p className="leading-relaxed" style={{ color: COLORS.gray600 }}>
                        {step.desc}
                      </p>
                    </motion.div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
    </section>
  );
};

// ============ FAQ ============
const FAQ = () => {
  const [open, setOpen] = useState(0);
  const faqs = [
    {
      q: 'What are the main advantages of permanent staffing over temporary/contract roles?',
      a: 'Permanent staffing offers several strategic advantages: (1) Institutional Knowledge: Employees develop deep understanding of your systems, architecture, and business goals over time. (2) Cost Efficiency: While upfront costs are higher, permanent placements reduce long-term hiring and training expenses. (3) Team Stability: Reduces turnover costs (which can exceed 150% of annual salary) and builds stronger team cohesion. (4) Culture Building: Permanent employees are invested in company culture and long-term success. (5) Training ROI: Investments in upskilling pay dividends as employees advance into leadership roles.',
    },
    {
      q: 'How long does your permanent staffing process typically take?',
      a: 'Our average timeline is 2-4 weeks from initial consultation to job acceptance. Here\'s the breakdown: Week 1 covers discovery and strategy. Week 2 involves sourcing and technical assessment. Weeks 3-4 include candidate presentations and interview coordination. Speed varies based on: (1) Role complexity and seniority level (senior architects may take 4-8 weeks). (2) Your decision-making process. (3) Market availability for niche tech skills. We prioritize quality over speed—ensuring every placement is technically sound and culturally aligned.',
    },
    {
      q: 'Do you handle different technology stacks and experience levels?',
      a: 'Yes, absolutely. We specialize across multiple tech domains and seniority levels: (1) Cloud & Infrastructure: AWS, Azure, GCP architects and engineers. (2) DevOps & SRE: CI/CD specialists, infrastructure automation experts. (3) AI/ML: Data scientists, ML engineers, NLP specialists. (4) Full-Stack Development: Frontend (React, Vue, Angular) and backend (Node.js, Java, Python) engineers. (5) Seniority Levels: Junior developers through Principal/Staff engineers. We also specialize in emerging areas like blockchain, IoT, and quantum computing.',
    },
    {
      q: 'Why should I work with Kapil Tech instead of other recruitment agencies?',
      a: 'Kapil Tech brings unique advantages: (1) Tech-Native Team: Our recruiters have engineering backgrounds and understand technical nuances deeply. (2) Pre-Screening: All candidates undergo rigorous technical assessments before presentation. (3) Market Insights: We provide salary benchmarks, skill trends, and hiring market data specific to your needs. (4) Extended Support: 90-day post-placement support ensures successful onboarding and integration. (5) Curated Networks: Direct access to passive candidates who aren\'t actively job hunting but are open to right opportunities. (6) 85% Success Rate: Our placement success rate demonstrates commitment to quality matches over volume.',
    },
    {
      q: 'How do you ensure cultural fit beyond technical skills?',
      a: 'Cultural fit is as important as technical expertise in our vetting process: (1) Deep Company Understanding: We spend time understanding your values, communication style, team dynamics, and growth trajectory. (2) Behavioral Assessment: Beyond technical interviews, we conduct behavioral interviews to evaluate work style and collaboration approach. (3) Reference Checks: We speak with previous managers to understand working relationships and cultural alignment. (4) Values Alignment: Candidates are assessed for shared values around innovation, learning, and team collaboration. (5) Communication Style: We match communication preferences (e.g., collaborative vs. independent) to team needs.',
    },
    {
      q: 'What happens after placement? Do you provide ongoing support?',
      a: 'Yes, ongoing support is a core part of our service: (1) 90-Day Integration Support: We check in regularly with both employee and employer during the critical onboarding period. (2) Success Metrics: We track productivity milestones, team integration, and job satisfaction. (3) Issue Resolution: Any adjustments or concerns are addressed promptly to ensure successful long-term fit. (4) Career Development: We provide guidance on growth opportunities and skill development to increase retention. (5) Ongoing Partnership: We remain your trusted recruiting partner for future staffing needs as your team scales.',
    },
  ];

  return (
    <section className="relative py-16 overflow-hidden" style={{ backgroundColor: COLORS.primary }} id="faq">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold leading-tight mb-6" style={{ color: COLORS.secondary }}>
            Frequently Asked
              Questions
          </h2>
        </motion.div>

        <div className="space-y-3 bg-white p-5 rounded-3xl border">
          {faqs.map((faq, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05 }}
              className="rounded-2xl border overflow-hidden transition-all"
              style={{
                backgroundColor: '#ffffff',
                borderColor: open === i ? 'rgba(99, 102, 241, 0.3)' : COLORS.gray200,
              }}
            >
              <button
                onClick={() => setOpen(open === i ? -1 : i)}
                className="w-full flex items-center justify-between gap-4 p-6 text-left"
              >
                <span className="font-semibold text-base md:text-lg" style={{ color: COLORS.primary }}>
                  {faq.q}
                </span>
                <motion.div
                  animate={{ rotate: open === i ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                  className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: open === i ? COLORS.accent : COLORS.gray200 }}
                >
                  <ChevronDown className="w-4 h-4" style={{ color: open === i ? COLORS.white : COLORS.primary }} />
                </motion.div>
              </button>

              <AnimatePresence>
                {open === i && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="px-6 pb-6 leading-relaxed text-justify" style={{ color: COLORS.gray600, wordBreak: 'normal',overflowWrap: 'normal',hyphens: 'none',whieSpace: 'normal' }}>
                      {faq.a}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============ FINAL CTA ============
const FinalCTA = () => {
  const COLORS = {
    bg: '#F8FAFC',
    heading: '#0F172A',
    muted: '#475569',
    accent: '#6366F1',
    accentLight: '#A5B4FC',
    white: '#FFFFFF',
    border: '#E2E8F0',
  };

  return (
    <section
      className="relative py-24 overflow-hidden"
      style={{ backgroundColor: COLORS.bg }}
    >
      <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2
            className="text-3xl md:text-4xl lg:text-5xl font-semibold leading-tight mb-6"
            style={{ color: COLORS.heading }}
          >
            Ready to Build Your
              Dream Tech Team?
          </h2>

          <p
            className="text-base md:text-lg max-w-2xl mx-auto mb-10"
            style={{ color: COLORS.muted }}
          >
            Let's connect you with permanent talent that transforms your
            organization.
          </p>

          <div className="flex flex-wrap items-center justify-center gap-4">
            <Link 
            to="/contact"
            onClick={() => window.scrollTo(0, 0)}
                whileHover={{ scale: 1.03, y: -1 }}
                whileTap={{ scale: 0.98 }}
                className="flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold text-base"
                style={{
                  backgroundColor: COLORS.accent,
                  color: COLORS.white,
                }}
              >
                Schedule Consultation Now
                <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
};


// ============ MAIN APP ============
export default function PermanentStaffing() {
  return (
    <div style={{ backgroundColor: COLORS.secondary, fontFamily: 'Inter, system-ui, sans-serif' }}>
      <Hero />
      <WhyStaffing />
      <TechExpertise />
      <Process />
      <FAQ />
      <FinalCTA />
    </div>
  );
}
